import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Icon } from "./Icon";

type SoundCue = "select" | "page-turn" | "reveal" | "complete";

const cueVolume: Record<SoundCue, number> = {
  select: 0.45,
  "page-turn": 0.5,
  reveal: 0.42,
  complete: 0.42,
};

interface AudioExperienceValue {
  enabled: boolean;
  play: (cue: SoundCue) => void;
  toggle: () => void;
}

const AudioExperienceContext = createContext<AudioExperienceValue>({
  enabled: false,
  play: () => undefined,
  toggle: () => undefined,
});

function playFile(cue: SoundCue) {
  const audio = new Audio(`/audio/${cue}.mp3`);
  audio.volume = cueVolume[cue];
  void audio.play().catch(() => undefined);
}

export function AudioExperienceProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabled] = useState(() => {
    try {
      return window.localStorage.getItem("lifescapsule:sound") === "on";
    } catch {
      return false;
    }
  });

  const play = useCallback(
    (cue: SoundCue) => {
      if (enabled) playFile(cue);
    },
    [enabled],
  );

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("lifescapsule:sound", next ? "on" : "off");
      } catch {
        // Preference storage is optional; sound still works for this visit.
      }
      if (next) playFile("select");
      return next;
    });
  }, []);

  const value = useMemo(() => ({ enabled, play, toggle }), [enabled, play, toggle]);
  return (
    <AudioExperienceContext.Provider value={value}>{children}</AudioExperienceContext.Provider>
  );
}

// This hook intentionally shares the provider module so its context stays private.
// eslint-disable-next-line react-refresh/only-export-components
export function useAudioExperience() {
  return useContext(AudioExperienceContext);
}

export function SoundToggle() {
  const { enabled, toggle } = useAudioExperience();
  return (
    <button
      className="sound-toggle"
      type="button"
      aria-pressed={enabled}
      onClick={toggle}
      title="Optional interface sounds"
    >
      <Icon name={enabled ? "sound" : "soundOff"} size={17} />
      <span>Sound {enabled ? "on" : "off"}</span>
    </button>
  );
}
